import * as Sentry from "@sentry/react";
import type { HeavyArtifactResponse } from "@/types/artifacts.ts";
import type { ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";

/**
 * Catches render errors inside any artifact body so a single broken artifact
 * degrades to a "Failed to render" card instead of taking down its siblings
 * or flooding the console with unattributed errors. Used by both the inline
 * thread view and the lightbox-mounted body (shared via
 * `HeavyArtifactLightbox`). Logs a structured object the next time we hit
 * something like BIG-4199 so we have a usable trace.
 */
export const ArtifactErrorBoundary = ({
  artifact,
  threadId,
  messageId,
  userIsAdmin = false,
  children,
}: {
  /** Only ever read for diagnostics, so the narrowest shape that supplies them
   *  — which is also what lets a redacted shared payload be passed here. */
  artifact: Pick<HeavyArtifactResponse, "id" | "artifactType" | "artifactTypeTitle">;
  /** Diagnostic context. Absent on a shared view, which has no thread. */
  threadId?: number;
  messageId?: number;
  userIsAdmin?: boolean;
  children: ReactNode;
}) => {
  return (
    <ErrorBoundary
      onError={(error) => {
        console.error("ArtifactContent: render failed", {
          artifactId: artifact.id,
          artifactType: artifact.artifactType,
          threadId,
          messageId,
          error,
        });
        Sentry.captureException(error, {
          tags: {
            component: "ArtifactErrorBoundary",
            artifactType: artifact.artifactType,
          },
          extra: {
            artifactId: artifact.id,
            threadId,
            messageId,
          },
        });
      }}
      fallbackRender={({ error }) => (
        <div className="px-4 py-3 text-sm text-muted-foreground">
          Failed to render {artifact.artifactTypeTitle}.
          {userIsAdmin && (
            <div className="mt-2 text-xs admin-only bg-red-100 p-3 rounded-lg overflow-x-auto">
              <div>
                <span className="text-destructive">Error:</span>{" "}
                {error instanceof Error ? error.message : String(error)}
              </div>
              <div className="mt-1">
                artifactId: {artifact.id} ({artifact.artifactType})
              </div>
            </div>
          )}
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
};
